import React, { useMemo, useRef } from "react";
import { DownloadSimple, Certificate } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadPNG, downloadSVG } from "@/lib/download";
import { buildCertificateSVG } from "@/lib/svgBuilders";
import { getTierConfig } from "@/components/dashboard/FoundingBadge";

export const CertificateCard = ({ user }) => {
    const containerRef = useRef(null);
    const cfg = getTierConfig(user?.founder_tier);
    const number = user ? String(user.member_number).padStart(4, "0") : "0000";
    const filenameBase = `eviq-certificate-${number}`;

    const svgHtml = useMemo(
        () =>
            user
                ? buildCertificateSVG({
                      user,
                      color: cfg.color,
                      tierLabel: cfg.label,
                      tierCap: cfg.cap,
                      memberNumber: number,
                  })
                : "",
        [user, cfg.color, cfg.label, cfg.cap, number]
    );

    if (!user) return null;

    const getSvg = () => containerRef.current?.querySelector("svg");

    const handlePNG = async () => {
        await downloadPNG(getSvg(), `${filenameBase}.png`, {
            width: 850,
            height: 1100,
            scale: 2,
        });
        toast.success("Certificate saved", {
            description: "1700×2200 PNG · print at letter or A4.",
        });
    };
    const handleSVG = () => {
        downloadSVG(getSvg(), `${filenameBase}.svg`);
        toast.success("SVG saved");
    };

    return (
        <div
            data-testid="certificate-card"
            className="grid grid-cols-1 gap-6 rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8 md:grid-cols-[220px_1fr] md:items-center md:gap-8"
        >
            <div className="mx-auto md:mx-0">
                <div
                    ref={containerRef}
                    data-testid="certificate-preview"
                    className="overflow-hidden rounded-md border border-[#1f1f22] [&>svg]:block [&>svg]:h-auto [&>svg]:w-[200px]"
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                />
            </div>
            <div>
                <div className="flex items-center gap-2 text-zinc-500">
                    <Certificate size={14} weight="fill" color={cfg.color} />
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                        Certificate of founding membership
                    </span>
                </div>
                <h3 className="mt-3 font-editorial text-xl leading-tight text-white sm:text-2xl">
                    A framed keepsake for the record.
                </h3>
                <p className="mt-2 text-sm text-zinc-500">
                    Numbered, non-transferable, and dated. Print at letter or A4 — matte paper
                    recommended.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                    <button
                        type="button"
                        data-testid="cert-download-png-btn"
                        onClick={handlePNG}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                        style={{ background: cfg.color }}
                    >
                        <DownloadSimple size={14} weight="bold" />
                        Download PNG
                    </button>
                    <button
                        type="button"
                        data-testid="cert-download-svg-btn"
                        onClick={handleSVG}
                        className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white"
                    >
                        Print-ready SVG
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CertificateCard;
